// src/pages/violations/ViolationDetail.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import DOMPurify from "dompurify";
import ReactDOM from "react-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";
import { FiPaperclip, FiUsers } from "react-icons/fi";
import { BASE_URL } from "../../utils";

const safeArr = (v) => (Array.isArray(v) ? v : []);
const toStr = (v) => (v == null ? "" : String(v));
const onlyDigits = (v) => toStr(v).replace(/\D+/g, "");

function pad2(n) {
  return String(n).padStart(2, "0");
}

function formatDateMDY(dateLike) {
  if (!dateLike && dateLike !== 0) return "";
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return "";
  const mm = pad2(d.getMonth() + 1);
  const dd = pad2(d.getDate());
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
}

function isTruthy(value) {
  const v = String(value || "").trim().toLowerCase();
  return v === "1" || v === "true" || v === "yes" || v === "y";
}

function MetaRow({ label, value }) {
  return (
    <div className="flex flex-col gap-1">
      <div className="text-base font-semibold text-[var(--fg)] leading-5">{label}</div>
      <div className="text-sm font-normal text-[var(--fg-muted)] break-words leading-5">
        {value || "-"}
      </div>
    </div>
  );
}

const stripHtml = (html) => {
  if (!html) return "";
  return String(html).replace(/<[^>]*>/g, "").replace(/\u00A0/g, " ").trim();
};

const sanitizeForView = (html) => {
  if (!html) return "";
  let s = String(html);
  s = s.replace(/<(script|style)[\s\S]*?>[\s\S]*?<\/\1>/gi, "");
  s = s.replace(/\son\w+=(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, "");
  try {
    s = DOMPurify.sanitize(s, { ADD_ATTR: ["target", "class", "style", "data-*"] });
  } catch (e) {}
  return s;
};

function safeParseFamilyHistory(v) {
  try {
    if (!v) return { summary: "", relations: [] };
    if (typeof v === "object") {
      return {
        summary: typeof v.summary === "string" ? v.summary : "",
        relations: Array.isArray(v.relations) ? v.relations : [],
      };
    }
    const obj = JSON.parse(String(v));
    return {
      summary: typeof obj?.summary === "string" ? obj.summary : "",
      relations: Array.isArray(obj?.relations) ? obj.relations : [],
    };
  } catch {
    return { summary: "", relations: [] };
  }
}

function hasMeaningfulFamilyHistory(rec) {
  if (!rec) return false;
  const fh = rec.family_history;
  if (fh === null || fh === undefined) return false;

  if (typeof fh === "object") {
    const relations = Array.isArray(fh.relations) ? fh.relations : fh.relations == null ? [] : null;
    const summary =
      typeof fh.summary === "string"
        ? fh.summary.trim()
        : fh.summary == null
        ? ""
        : String(fh.summary).trim();
    if (Array.isArray(relations) && relations.length === 0 && summary === "") return false;
    if ((Array.isArray(relations) && relations.length > 0) || summary !== "") return true;
    return false;
  }

  if (typeof fh === "string") {
    const trimmed = fh.trim();
    if (trimmed === "") return false;
    const lower = trimmed.toLowerCase();
    if (lower === "na") return false;
    if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
      try {
        const parsed = JSON.parse(trimmed);
        const rels = Array.isArray(parsed?.relations) ? parsed.relations : [];
        const summ = (parsed?.summary ?? "").toString().trim();
        if (rels.length === 0 && summ === "") return false;
        return true;
      } catch (e) {}
    }
    return true;
  }

  return false;
}

function NoteItem({ note, onEdit, onDelete, onUpdate, renderNoteContent }) {
  const [isEditingInline, setIsEditingInline] = useState(false);
  const [editedText, setEditedText] = useState(note.text || "");

  useEffect(() => {
    setEditedText(note.text || "");
  }, [note.text]);

  const handleSaveInline = async () => {
    if (!onUpdate) return;
    await onUpdate(editedText);
    setIsEditingInline(false);
  };

  const buildPdfHrefLocal = (pdf_url) => {
    if (!pdf_url) return null;
    if (pdf_url.startsWith("http://") || pdf_url.startsWith("https://")) return pdf_url;
    const path = pdf_url.startsWith("/") ? pdf_url : `/${pdf_url}`;
    return `${BASE_URL}${path}`;
  };

  const pdfHref = note.pdf_url || note.pdfUrl ? buildPdfHrefLocal(note.pdf_url || note.pdfUrl) : null;

  return (
    <div className="text-sm mb-3 pl-2 border-l-4 border-gray-300">
      {isEditingInline ? (
        <>
          <textarea
            value={editedText}
            onChange={(e) => setEditedText(e.target.value)}
            className="w-full text-sm border px-2 py-1 rounded"
          />
          <div className="flex gap-2 mt-1">
            <button onClick={handleSaveInline} className="text-green-600 text-xs font-semibold">
              Save
            </button>
            <button onClick={() => setIsEditingInline(false)} className="text-gray-500 text-xs">
              Cancel
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="text-gray-800 mb-2" dangerouslySetInnerHTML={renderNoteContent(note.text)} />

          {pdfHref ? (
            <div className="mb-2">
              <a
                href={pdfHref}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-blue-600 underline inline-flex items-center gap-1"
                title="Open attached PDF"
              >
                <FiPaperclip size={14} /> View Attached PDF
              </a>
            </div>
          ) : null}

          <div className="text-xs text-gray-500 flex flex-col sm:flex-row sm:justify-between gap-2">
            <span className="block">{note.timestamp}</span>
            <div className="flex items-center gap-3">
              <button
                onClick={() => onEdit && onEdit(note)}
                className="text-indigo-500 text-xs inline-flex items-center gap-1"
              >
                Edit
              </button>
              <button onClick={() => onDelete && onDelete(note)} className="text-red-500 text-xs">
                Delete
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function DescriptionCell({ value, label = "Description", limit = 20 }) {
  const full = value == null ? "" : String(value).trim();
  const isLong = full.length > limit;
  const short = isLong ? `${full.slice(0, limit)}...` : full;
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 320 });

  useEffect(() => {
    if (!open) return;

    const updatePos = () => {
      const el = anchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setPos({
        top: r.bottom + 8,
        left: Math.min(r.left, window.innerWidth - 460),
        width: Math.min(420, Math.max(260, r.width)),
      });
    };

    updatePos();

    const onDocMouseDown = (e) => {
      const el = anchorRef.current;
      if (!el) return;
      if (!el.contains(e.target)) setOpen(false);
    };

    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };

    window.addEventListener("scroll", updatePos, true);
    window.addEventListener("resize", updatePos);
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("scroll", updatePos, true);
      window.removeEventListener("resize", updatePos);
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  if (!full) return <span className="text-slate-400">-</span>;

  return (
    <div ref={anchorRef} className="w-full">
      <div className="flex items-center gap-2 w-full">
        <span className="block flex-1 min-w-0 truncate text-sm" style={{ userSelect: "text" }}>
          {short}
        </span>

        {isLong && (
          <button
            type="button"
            className="shrink-0 text-[11px] text-[var(--accent)] underline hover:text-[var(--accent-strong)]"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setOpen((v) => !v);
            }}
          >
            {open ? "Hide" : "View more"}
          </button>
        )}
      </div>

      {open &&
        ReactDOM.createPortal(
          <div
            className="fixed z-[999999]"
            style={{ top: pos.top, left: pos.left }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="rounded-lg border border-slate-200 bg-white shadow-xl p-2 text-xs text-slate-800 whitespace-normal break-words w-[420px] max-w-[calc(100vw-24px)]">
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="font-semibold text-slate-700">{label}</div>
                <button
                  className="text-[11px] text-slate-500 hover:text-slate-800"
                  onClick={() => setOpen(false)}
                  type="button"
                >
                  x
                </button>
              </div>
              <div style={{ userSelect: "text" }}>{full}</div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

function DetailModal({ open, row, onClose }) {
  if (!open || !row) return null;
  const HIDDEN_KEYS = new Set([
    "email1",
    "email2",
    "email3",
    "phone_no1",
    "phone_no2",
    "phone_no3",
    "placeholder1",
    "placeholder2",
    "placeholder3",
    "placeholder4",
    "placeholder5",
    "placeholder6",
    "placeholder7",
    "placeholder8",
    "placeholder9",
    "placeholder10",
  ]);
  const entries = Object.entries(row || {}).filter(([key]) => !HIDDEN_KEYS.has(key));
  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center bg-black bg-opacity-40 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white rounded shadow-lg w-full max-w-4xl p-4 max-h-[80vh] overflow-auto">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold">Violation Detail</h3>
          <button onClick={onClose} className="px-3 py-1 border rounded text-sm">
            Close
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {entries.map(([key, value]) => (
            <div key={key} className="border rounded p-2 bg-white">
              <div className="text-xs font-semibold text-slate-600 mb-1">{key}</div>
              <div className="text-sm text-slate-800 break-words">{value == null || value === "" ? "-" : String(value)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
}

function FamilyHistoryModal({ open, onClose, onSave, saving, summary, setSummary, members, setMembers }) {
  if (!open) return null;

  const addMember = () => {
    setMembers((prev) => [
      ...(Array.isArray(prev) ? prev : []),
      {
        id: `m_${Date.now()}`,
        name: "",
        relationship: "",
        contacts: [{ phone: "", address: "", email: "" }],
      },
    ]);
  };

  const removeMember = (memberIndex) => {
    setMembers((prev) => {
      const arr = Array.isArray(prev) ? prev : [];
      const next = arr.filter((_, i) => i !== memberIndex);
      return next.length ? next : [];
    });
  };

  const updateMemberField = (memberIndex, key, value) => {
    setMembers((prev) => {
      const arr = Array.isArray(prev) ? prev : [];
      return arr.map((m, i) => (i === memberIndex ? { ...m, [key]: value } : m));
    });
  };

  const addContactRow = (memberIndex) => {
    setMembers((prev) => {
      const arr = Array.isArray(prev) ? prev : [];
      return arr.map((m, i) => {
        if (i !== memberIndex) return m;
        const contacts = Array.isArray(m.contacts) ? m.contacts.map((c) => ({ ...c })) : [];
        contacts.push({ phone: "", address: "", email: "" });
        return { ...m, contacts };
      });
    });
  };

  const removeContactRow = (memberIndex, contactIndex) => {
    setMembers((prev) => {
      const arr = Array.isArray(prev) ? prev : [];
      return arr.map((m, i) => {
        if (i !== memberIndex) return m;
        const contacts = Array.isArray(m.contacts) ? m.contacts.map((c) => ({ ...c })) : [];
        const next = contacts.filter((_, ci) => ci !== contactIndex);
        return { ...m, contacts: next.length ? next : [{ phone: "", address: "", email: "" }] };
      });
    });
  };

  const updateContactField = (memberIndex, contactIndex, key, value) => {
    setMembers((prev) => {
      const arr = Array.isArray(prev) ? prev : [];
      return arr.map((m, i) => {
        if (i !== memberIndex) return m;
        const contacts = Array.isArray(m.contacts)
          ? m.contacts.map((c) => ({ ...c }))
          : [{ phone: "", address: "", email: "" }];
        while (contacts.length <= contactIndex) contacts.push({ phone: "", address: "", email: "" });
        contacts[contactIndex][key] = value;
        return { ...m, contacts };
      });
    });
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[99998] flex items-center justify-center bg-black bg-opacity-40 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white rounded shadow-lg w-full max-w-6xl p-4 max-h-[85vh] overflow-auto">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-lg font-semibold">Family History (edit)</h3>
            <div className="text-xs text-gray-500">Add, edit, or remove relationships and contacts.</div>
          </div>

          <div className="flex items-center gap-2">
            <button onClick={onClose} className="ui-btn">
              Close
            </button>
            <button
              onClick={onSave}
              disabled={saving}
              className={`ui-btn ui-btn-primary ${saving ? "opacity-60" : ""}`}
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>

        <div className="mb-3">
          <label className="block text-xs font-semibold text-[var(--fg-muted)] mb-1">Summary</label>
          <textarea
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            className="ui-input w-full"
            rows={3}
            placeholder="Summary..."
          />
        </div>

        <div className="mb-3">
          <button
            type="button"
            onClick={addMember}
            className="ui-btn"
          >
            + Add Family Member
          </button>
        </div>

        <div className="border rounded overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--surface-muted)] border-b border-[var(--border)]">
              <tr>
                <th className="px-3 py-2 text-xs font-semibold text-[var(--fg-muted)] text-left w-[180px]">Name</th>
                <th className="px-3 py-2 text-xs font-semibold text-[var(--fg-muted)] text-left w-[140px]">Relationship</th>
                <th className="px-3 py-2 text-xs font-semibold text-[var(--fg-muted)] text-left">Contacts (phone / address / email)</th>
                <th className="px-3 py-2 text-xs font-semibold text-[var(--fg-muted)] text-center w-[90px]">
                  Remove
                </th>
              </tr>
            </thead>

            <tbody>
              {(members || []).length === 0 ? (
                <tr>
                  <td colSpan={4} className="p-3 text-center text-xs text-gray-500">
                    No family members yet. Click "Add Family Member".
                  </td>
                </tr>
              ) : null}

              {(members || []).map((m, idx) => (
                <tr key={m.id || idx} className="border-t">
                  <td className="p-2 align-top">
                    <input
                      value={m.name || ""}
                      onChange={(e) => updateMemberField(idx, "name", e.target.value)}
                      className="ui-input w-full"
                      placeholder="Name"
                    />
                  </td>

                  <td className="p-2 align-top">
                    <input
                      value={m.relationship || ""}
                      onChange={(e) => updateMemberField(idx, "relationship", e.target.value)}
                      className="ui-input w-full"
                      placeholder="Relationship"
                    />
                  </td>

                  <td className="p-2 align-top">
                    <div className="border rounded p-2 bg-white">
                      <div className="space-y-2">
                        {(Array.isArray(m.contacts) ? m.contacts : [{ phone: "", address: "", email: "" }]).map(
                          (c, ci) => (
                            <div key={ci} className="grid grid-cols-[220px_1fr_1fr_90px] gap-2 items-center">
                              <input
                                value={c.phone ?? ""}
                                inputMode="numeric"
                                pattern="[0-9]*"
                                maxLength={15}
                                onChange={(e) => updateContactField(idx, ci, "phone", onlyDigits(e.target.value))}
                                onKeyDown={(e) => {
                                  const allowed = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"];
                                  if (!allowed.includes(e.key) && !/^\d$/.test(e.key)) e.preventDefault();
                                }}
                                onPaste={(e) => {
                                  e.preventDefault();
                                  const pasted = e.clipboardData.getData("text");
                                  updateContactField(idx, ci, "phone", onlyDigits(pasted));
                                }}
                                className="ui-input w-full font-mono"
                                placeholder="Phone"
                              />

                              <input
                                value={c.address ?? ""}
                                onChange={(e) => updateContactField(idx, ci, "address", e.target.value)}
                                className="ui-input w-full"
                                placeholder="Address"
                              />

                              <input
                                value={c.email ?? ""}
                                onChange={(e) => updateContactField(idx, ci, "email", e.target.value)}
                                className="ui-input w-full"
                                placeholder="Email"
                              />

                              <button
                                type="button"
                                onClick={() => removeContactRow(idx, ci)}
                                className="ui-btn text-xs text-red-600"
                              >
                                Remove
                              </button>
                            </div>
                          )
                        )}

                        <div className="pt-1">
                          <button
                            type="button"
                            onClick={() => addContactRow(idx)}
                            className="ui-btn text-xs inline-flex items-center gap-2"
                          >
                            Add contact row
                          </button>
                        </div>
                      </div>
                    </div>
                  </td>

                  <td className="px-3 py-2 text-center align-top">
                    <button
                      type="button"
                      onClick={() => removeMember(idx)}
                      className="ui-btn text-xs text-red-600"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function ViolationDetail() {
  const { bbl } = useParams();
  const navigate = useNavigate();

  const [records, setRecords] = useState([]);
  const [recordPage, setRecordPage] = useState(1);
  const recordPageSize = 5;
  const [recordTotal, setRecordTotal] = useState(0);
  const [headerRecord, setHeaderRecord] = useState(null);
  const [bblInfo, setBblInfo] = useState({ bbl: "", boro: "", block: "", lot: "", total: 0 });
  const [familyHistoryRaw, setFamilyHistoryRaw] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [fhModalOpen, setFhModalOpen] = useState(false);
  const [fhSummary, setFhSummary] = useState("");
  const [fhMembers, setFhMembers] = useState([]);
  const [fhSaving, setFhSaving] = useState(false);

  const [notes, setNotes] = useState([]);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailRow, setDetailRow] = useState(null);
  const editorRef = useRef(null);
  const [appliedRawHtml, setAppliedRawHtml] = useState("");
  const [showPasteHtml, setShowPasteHtml] = useState(false);
  const [pasteHtmlText, setPasteHtmlText] = useState("");
  const [currentNoteId, setCurrentNoteId] = useState(null);
  const [uploadedPdfUrl, setUploadedPdfUrl] = useState(null);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  const [activeStatus, setActiveStatus] = useState("Approved");
  const subStatusOptions = ["NA", "Research", "Follow Up", "Not Selling", "Evernote", "Selling", "fresh lead"];
  const [subStatus, setSubStatus] = useState("NA");
  const [subStatusManuallySet, setSubStatusManuallySet] = useState(false);
  const [label, setLabel] = useState("");
  const [showCalendar, setShowCalendar] = useState(false);
  const [followUpDate, setFollowUpDate] = useState("");

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${BASE_URL}/api/violations/bbl/${encodeURIComponent(bbl)}`, {
        params: { page: recordPage, pageSize: recordPageSize },
      });
      const recs = res.data?.records || [];
      setRecords(recs);
      const total = res.data?.total || 0;
      setRecordTotal(total);
      setBblInfo({
        bbl: res.data?.bbl || bbl,
        boro: res.data?.boro || "",
        block: res.data?.block || "",
        lot: res.data?.lot || "",
        total,
      });
      setHeaderRecord((prev) => {
        if (recordPage === 1) return recs[0] || null;
        if (!prev && recs[0]) return recs[0];
        return prev;
      });
      setFamilyHistoryRaw(res.data?.family_history || null);
    } catch (err) {
      console.error("violation BBL detail error", err);
      setError(err?.response?.data?.message || "Failed to load BBL detail");
    } finally {
      setLoading(false);
    }
  }, [bbl, recordPage, recordPageSize]);

  const fetchNotes = useCallback(async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/violations/bbl/${encodeURIComponent(bbl)}/notes`, {
        params: { page: 1, pageSize: 200 },
      });
      let arr = res?.data?.data || res?.data || [];
      if (!Array.isArray(arr)) arr = [];

      const normalized = arr.map((n) => ({
        id: n.id || n.noteId || n.note_id || null,
        text: n.text || n.note || n.html || n.note_html || "",
        plain_text: n.plain_text || n.plainText || n.plain || "",
        pdf_url: n.pdf_url || n.pdfUrl || n.url || null,
        timestamp: n.timestamp || "",
        status: n.status || n.state || null,
        sub_status: n.sub_status || n.subStatus || null,
        label: n.label || null,
        followUp_date: n.followUp_date || n.followUpDate || null,
        raw: n,
      }));

      setNotes(normalized);

      if (normalized.length > 0) {
        const latest = normalized[0];
        const derivedStatus = latest.status || "Approved";
        const derivedSub = latest.sub_status || "NA";
        const derivedLabel = latest.label || "";

        setActiveStatus(derivedStatus);
        setSubStatus(derivedSub);
        setLabel(derivedLabel);
        setSubStatusManuallySet(false);

        if (String(derivedSub).toLowerCase() === "follow up" && latest.followUp_date) {
          try {
            const fd = new Date(latest.followUp_date);
            if (!Number.isNaN(fd.getTime())) setFollowUpDate(fd.toISOString().split("T")[0]);
          } catch (e) {}
        } else {
          setFollowUpDate(latest.followUp_date || "");
        }
        setShowCalendar(false);
      }
    } catch (err) {
      console.warn("violation notes fetch failed", err);
      setNotes([]);
    }
  }, [bbl]);

  useEffect(() => {
    fetchDetail();
    fetchNotes();
  }, [fetchDetail, fetchNotes]);

  useEffect(() => {
    setRecordPage(1);
    setHeaderRecord(null);
  }, [bbl]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil((recordTotal || 0) / recordPageSize));
    if (recordPage > maxPage) setRecordPage(maxPage);
  }, [recordPage, recordPageSize, recordTotal]);

  const openFamilyHistoryModal = useCallback(() => {
    const parsed = safeParseFamilyHistory(familyHistoryRaw);
    setFhSummary((parsed?.summary ?? "").toString());

    const rels = Array.isArray(parsed?.relations) ? parsed.relations : [];
    const members = rels.map((r, idx) => {
      const phonesRaw = Array.isArray(r?.phones) ? r.phones : r?.phone ? [r.phone] : [];
      const phones = phonesRaw.map((p) => onlyDigits(p)).filter((p) => p !== "");

      const emails = Array.isArray(r?.emails) ? r.emails : r?.email ? [r.email] : [];
      const addresses = Array.isArray(r?.addresses) ? r.addresses : r?.address ? [r.address] : [];

      const max = Math.max(phones.length, emails.length, addresses.length, 1);
      const contacts = [];
      for (let i = 0; i < max; i++) {
        contacts.push({
          phone: phones[i] ?? "",
          address: (addresses[i] ?? "").toString(),
          email: (emails[i] ?? "").toString(),
        });
      }

      return {
        id: r?.id || `fh_${idx}_${Date.now()}`,
        name: (r?.name ?? "").toString(),
        relationship: (r?.relationship ?? "").toString(),
        contacts,
      };
    });

    setFhMembers(members);
    setFhModalOpen(true);
  }, [familyHistoryRaw]);

  const saveFamilyHistoryManual = useCallback(async () => {
    if (!bbl) return;
    setFhSaving(true);

    try {
      const relations = (fhMembers || [])
        .map((m) => {
          const name = (m?.name ?? "").toString().trim();
          const relationship = (m?.relationship ?? "").toString().trim();

          const contacts = Array.isArray(m?.contacts) ? m.contacts : [];

          const phones = contacts.map((c) => onlyDigits(c?.phone ?? "")).filter(Boolean);
          const emails = contacts.map((c) => (c?.email ?? "").toString().trim()).filter(Boolean);
          const addresses = contacts.map((c) => (c?.address ?? "").toString().trim()).filter(Boolean);

          if (!name && !relationship && phones.length === 0 && emails.length === 0 && addresses.length === 0) return null;

          return {
            name: name || null,
            relationship: relationship || null,
            phones,
            emails,
            addresses,
            phone: phones[0] || null,
            email: emails[0] || null,
            address: addresses[0] || null,
          };
        })
        .filter(Boolean);

      const payload = {
        family_history: {
          summary: (fhSummary ?? "").toString(),
          relations,
        },
      };

      await axios.put(`${BASE_URL}/api/violations/bbl/${encodeURIComponent(bbl)}/family_history`, payload);
      toast.success("Family History saved.");
      setFhModalOpen(false);
      await fetchDetail();
    } catch (err) {
      console.error("saveFamilyHistoryManual error", err);
      const msg = err?.response?.data?.message || err?.response?.data?.error || "Failed to save family history";
      toast.error(String(msg));
    } finally {
      setFhSaving(false);
    }
  }, [bbl, fhSummary, fhMembers, fetchDetail]);

  const handleEditorPaste = (e) => {
    if (!e.clipboardData) return;
    e.preventDefault();
    const html = e.clipboardData.getData("text/html");
    const text = e.clipboardData.getData("text/plain");
    if (html) insertHtmlAtCaret(html);
    else if (text) insertHtmlAtCaret(text.replace(/\r\n|\r|\n/g, "<br>"));
  };

  function insertHtmlAtCaret(html) {
    try {
      document.execCommand("insertHTML", false, html);
      return;
    } catch (err) {}
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) {
      if (editorRef.current) editorRef.current.insertAdjacentHTML("beforeend", html);
      return;
    }
    const range = sel.getRangeAt(0);
    range.deleteContents();
    const el = document.createElement("div");
    el.innerHTML = html;
    const frag = document.createDocumentFragment();
    let node;
    while ((node = el.firstChild)) frag.appendChild(node);
    range.insertNode(frag);
    if (frag.lastChild) {
      range.setStartAfter(frag.lastChild);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }

  const applyPastedHtml = () => {
    try {
      if (!pasteHtmlText || !pasteHtmlText.trim()) {
        toast.error("No HTML provided.");
        return;
      }
      const sanitized = DOMPurify.sanitize(pasteHtmlText, {
        ALLOWED_ATTR: ["style", "class", "src", "href", "alt", "title", "target", "data-*"],
      });
      if (!sanitized || sanitized.trim() === "") {
        toast.error("Sanitization removed all content. Paste different HTML.");
        return;
      }
      setAppliedRawHtml(sanitized);
      if (editorRef.current) editorRef.current.innerHTML = sanitized;
      setShowPasteHtml(false);
      setPasteHtmlText("");
      setCurrentNoteId(null);
      toast.success("Raw HTML applied. Click Save to persist.");
    } catch (err) {
      console.error("applyPastedHtml error", err);
      toast.error("Failed to apply pasted HTML.");
    }
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) return toast.error("Please upload a PDF file.");
    const formData = new FormData();
    formData.append("pdf", file);
    try {
      setUploadingPdf(true);
      const uploadRes = await axios.post(`${BASE_URL}/api/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const uploadedUrl =
        uploadRes?.data?.url || uploadRes?.data?.fileURL || uploadRes?.data?.fileUrl || uploadRes?.data?.url;
      if (!uploadedUrl) throw new Error("Upload returned no URL.");
      setUploadedPdfUrl(uploadedUrl);
      toast.success("PDF uploaded successfully and ready to attach.");
    } catch (err) {
      console.error("PDF upload error", err);
      toast.error("Failed to upload PDF.");
    } finally {
      setUploadingPdf(false);
    }
  };

  const updateNoteInline = async (note, newText) => {
    const noteId = note.id || note.noteId;
    if (!noteId) throw new Error("Missing note id");
    try {
      await axios.put(`${BASE_URL}/api/violations/bbl/${encodeURIComponent(bbl)}/notes/${noteId}`, {
        note: newText,
        plain_text: stripHtml(newText),
        status: note.status || activeStatus,
        sub_status: note.sub_status || subStatus,
        label: note.label || label,
      });
      toast.success("Note updated.");
      await fetchNotes();
    } catch (err) {
      console.error("inline update error", err);
      toast.error("Failed to update note");
      throw err;
    }
  };

  const deleteNote = async (note) => {
    const nid = note.id || note.noteId;
    if (!nid) return;
    if (!window.confirm("Delete this note?")) return;
    try {
      await axios.delete(`${BASE_URL}/api/violations/bbl/${encodeURIComponent(bbl)}/notes/${nid}`);
      toast.success("Note deleted");
      await fetchNotes();
    } catch (err) {
      console.error("delete note error", err);
      toast.error("Failed to delete note");
    }
  };

  const loadNoteIntoEditorForEdit = (note) => {
    if (!note) return;
    setCurrentNoteId(note.id || note.noteId || null);
    setAppliedRawHtml("");
    if (editorRef.current) editorRef.current.innerHTML = note.text || note.note || "";
    setUploadedPdfUrl(note.pdf_url || note.pdfUrl || null);
    window.scrollTo({ top: 0, behavior: "smooth" });
    toast.info("Loaded note into editor for editing.");
  };

  const onSubStatusChange = (selected) => {
    setSubStatus(selected);
    setSubStatusManuallySet(true);
    setShowCalendar(String(selected) === "Follow Up");
    if (selected !== "Follow Up") setFollowUpDate("");
  };

  const handleFollowUpPick = (date) => {
    if (!date) return;
    const formattedDisplay = date.toLocaleDateString("en-US");
    const isoDate = date.toISOString().split("T")[0];
    setFollowUpDate(isoDate);
    const followHtml = `<p><strong>Follow-up on: ${formattedDisplay}</strong></p>`;
    const currentHtml =
      appliedRawHtml && appliedRawHtml.trim()
        ? appliedRawHtml
        : (editorRef.current ? editorRef.current.innerHTML : "") || "";
    const combined = followHtml + (currentHtml || "");
    if (editorRef.current) editorRef.current.innerHTML = combined;
    setAppliedRawHtml("");
    setShowCalendar(false);
  };

  const handleSaveStatus = async () => {
    const editorHtml = editorRef.current ? editorRef.current.innerHTML : "";
    const htmlToSave = appliedRawHtml && appliedRawHtml.trim() ? appliedRawHtml.trim() : (editorHtml || "").trim();
    const plain = stripHtml(htmlToSave);

    if (activeStatus === "Rejected" && !plain.trim()) {
      return toast.error("Please enter a note before saving a Rejected status.");
    }

    if (activeStatus !== "Rejected" && !plain.trim() && !uploadedPdfUrl) {
      return toast.error("Please enter a note or attach a PDF before saving status.");
    }

    if (activeStatus === "Approved") {
      if (!label || label === "") {
        return toast.error("Please select a Label before saving.");
      }
    }

    if (subStatus === "Follow Up" && subStatusManuallySet) {
      const match = plain.match(/\b\d{1,2}\/\d{1,2}\/\d{4}\b/);
      if (!match && !followUpDate) {
        return toast.error("Please include a follow-up date in the note or pick the follow-up calendar.");
      }
    }

    setSaveLoading(true);
    try {
      const payload = {
        status: activeStatus,
        note: htmlToSave || null,
        plain_text: stripHtml(htmlToSave) || null,
        sub_status: subStatus || null,
        label: label || null,
        followUp_date: followUpDate || null,
        pdf_url: uploadedPdfUrl || null,
        created_by: null,
        updated_by: null,
      };

      if (currentNoteId) {
        await axios.put(`${BASE_URL}/api/violations/bbl/${encodeURIComponent(bbl)}/notes/${currentNoteId}`, payload);
      } else {
        await axios.post(`${BASE_URL}/api/violations/bbl/${encodeURIComponent(bbl)}/notes`, payload);
      }

      toast.success("Status and note saved.");

      setAppliedRawHtml("");
      if (editorRef.current) editorRef.current.innerHTML = "";
      setCurrentNoteId(null);
      setUploadedPdfUrl(null);

      await fetchNotes();
    } catch (err) {
      console.error("Error saving status:", err);
      const msg = err?.response?.data?.message || "Failed to save status.";
      toast.error(msg);
    } finally {
      setSaveLoading(false);
    }
  };

  const recordAddress = useMemo(() => {
    const first = headerRecord || (records && records[0] ? records[0] : null);
    if (!first) return "";
    return [first.house_number, first.street, first.city, first.zip]
      .filter((v) => v != null && String(v).trim() !== "")
      .join(" ");
  }, [headerRecord, records]);

  const totalRecordPages = Math.max(1, Math.ceil((recordTotal || 0) / recordPageSize));
  const recordStart = recordTotal === 0 ? 0 : (recordPage - 1) * recordPageSize + 1;
  const recordEnd = Math.min(recordTotal, recordPage * recordPageSize);
  const pagedRecords = records;

  if (loading) return <div className="p-4">Loading...</div>;

  if (error) {
    return (
      <div className="p-4">
        <div className="mb-3 text-red-600 text-sm">{error}</div>
        <button onClick={() => navigate("/violations")} className="px-3 py-1 border rounded text-sm">
          Back
        </button>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="filter-panel mb-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="page-kicker">Violations</div>
            <h2 className="page-title">Violation BBL Detail</h2>
            <div className="page-subtitle">BBL: {bblInfo.bbl || bbl}</div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate("/violations")} className="ui-btn">
              Back
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="surface-card rounded-2xl p-4">
          <h3 className="section-title mb-3">Property Summary</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <MetaRow label="BBL" value={bblInfo.bbl || bbl} />
            <MetaRow label="Violations" value={bblInfo.total || recordTotal} />
            <MetaRow label="Boro" value={bblInfo.boro} />
            <MetaRow label="Block" value={bblInfo.block} />
            <MetaRow label="Lot" value={bblInfo.lot} />
            <MetaRow label="Address" value={recordAddress || "-"} />
          </div>
        </div>

        <div className="surface-card rounded-2xl p-4">
          <h3 className="section-title mb-3">Latest Record</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <MetaRow label="Owner" value={headerRecord?.owner_name} />
            <MetaRow label="Source" value={headerRecord?.source} />
            <MetaRow label="Violation #" value={headerRecord?.violation_number} />
            <MetaRow label="Issue Date" value={formatDateMDY(headerRecord?.issue_date)} />
            <MetaRow label="Status" value={headerRecord?.current_status} />
            <MetaRow
              label="Rent Impairing"
              value={isTruthy(headerRecord?.rent_impairing) ? "Yes" : "No"}
            />
          </div>
        </div>
      </div>

      <div className="mt-6 surface-card rounded-2xl p-4">
        <h3 className="section-title mb-3">All Violations for This BBL</h3>
        <div className="overflow-auto">
          <table className="min-w-[900px] w-full text-sm">
            <thead className="bg-[var(--surface-muted)] border-b border-[var(--border)]">
              <tr>
                <th className="px-3 py-2 text-xs font-semibold text-[var(--fg-muted)] text-left">ID</th>
                <th className="px-3 py-2 text-xs font-semibold text-[var(--fg-muted)] text-left">Boro</th>
                <th className="px-3 py-2 text-xs font-semibold text-[var(--fg-muted)] text-left">Block</th>
                <th className="px-3 py-2 text-xs font-semibold text-[var(--fg-muted)] text-left">Lot</th>
                <th className="px-3 py-2 text-xs font-semibold text-[var(--fg-muted)] text-left">Violation #</th>
                <th className="px-3 py-2 text-xs font-semibold text-[var(--fg-muted)] text-left">Source</th>
                <th className="px-3 py-2 text-xs font-semibold text-[var(--fg-muted)] text-left">Issue Date</th>
                <th className="px-3 py-2 text-xs font-semibold text-[var(--fg-muted)] text-left">Type</th>
                <th className="px-3 py-2 text-xs font-semibold text-[var(--fg-muted)] text-left">Category</th>
                <th className="px-3 py-2 text-xs font-semibold text-[var(--fg-muted)] text-left">Description</th>
                <th className="px-3 py-2 text-xs font-semibold text-[var(--fg-muted)] text-left">Status</th>
                <th className="px-3 py-2 text-xs font-semibold text-[var(--fg-muted)] text-left">View</th>
              </tr>
            </thead>
            <tbody>
              {pagedRecords.length === 0 ? (
                <tr>
                  <td colSpan={12} className="p-3 text-xs text-gray-500">
                    No records found.
                  </td>
                </tr>
              ) : (
                pagedRecords.map((r) => (
                  <tr key={r.id} className="border-t">
                    <td className="px-3 py-2">{r.id || "-"}</td>
                    <td className="px-3 py-2">{r.boro || "-"}</td>
                    <td className="px-3 py-2">{r.block || "-"}</td>
                    <td className="px-3 py-2">{r.lot || "-"}</td>
                    <td className="px-3 py-2">{r.violation_number || "-"}</td>
                    <td className="px-3 py-2">{r.source || "-"}</td>
                    <td className="px-3 py-2">{formatDateMDY(r.issue_date)}</td>
                    <td className="px-3 py-2">{r.violation_type || "-"}</td>
                    <td className="px-3 py-2">{r.violation_category || "-"}</td>
                    <td className="px-3 py-2">
                      <DescriptionCell value={r.description} label="Description" limit={20} />
                    </td>
                    <td className="px-3 py-2">{r.current_status || r.ecb_violation_status || r.hmc_current_status || "-"}</td>
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        className="ui-btn ui-btn-primary h-7 px-3 text-[11px]"
                        onClick={() => {
                          setDetailRow(r);
                          setDetailModalOpen(true);
                        }}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="mt-3 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="text-sm text-slate-600">
            {recordTotal === 0 ? (
              <>No records</>
            ) : (
              <>
                Showing <strong>{recordStart}</strong> - <strong>{recordEnd}</strong> of{" "}
                <strong>{recordTotal}</strong>
              </>
            )}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setRecordPage(1)}
              disabled={recordPage <= 1}
              className="px-3 py-1 border rounded disabled:opacity-60 text-sm"
            >
              First
            </button>
            <button
              onClick={() => setRecordPage((p) => Math.max(1, p - 1))}
              disabled={recordPage <= 1}
              className="px-3 py-1 border rounded disabled:opacity-60 text-sm"
            >
              Prev
            </button>

            <div className="text-sm text-slate-600">
              Page <strong>{recordPage}</strong> of <strong>{totalRecordPages}</strong>
            </div>

            <button
              onClick={() => setRecordPage((p) => Math.min(totalRecordPages, p + 1))}
              disabled={recordPage >= totalRecordPages}
              className="px-3 py-1 border rounded disabled:opacity-60 text-sm"
            >
              Next
            </button>
            <button
              onClick={() => setRecordPage(totalRecordPages)}
              disabled={recordPage >= totalRecordPages}
              className="px-3 py-1 border rounded disabled:opacity-60 text-sm"
            >
              Last
            </button>
          </div>
        </div>
      </div>

      <div className="mt-6">
        <div className="surface-card rounded-2xl p-4">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div>
              <h3 className="section-title mb-0 flex items-center gap-2">
                <FiUsers /> Family History (Manual)
              </h3>
              <div className="section-subtitle">This is separate from violation records.</div>
            </div>

            <button
              onClick={openFamilyHistoryModal}
              className="ui-btn"
              title="Edit Family History"
            >
              Edit
            </button>
          </div>

          {hasMeaningfulFamilyHistory({ family_history: familyHistoryRaw }) ? (
            <div className="mt-3">
              {(() => {
                const parsed = safeParseFamilyHistory(familyHistoryRaw);
                const fhSummaryParsed = (parsed?.summary ?? "").toString();
                const fhRelationsParsed = Array.isArray(parsed?.relations) ? parsed.relations : [];

                return (
                  <>
                    <div className="mb-3">
                      <div className="text-xs text-gray-500 mb-1">Summary</div>
                      <div className="text-sm text-gray-800 whitespace-pre-line">
                        {fhSummaryParsed?.trim() ? fhSummaryParsed : "-"}
                      </div>
                    </div>

                    <div className="border rounded bg-white overflow-auto">
                      <table className="w-full text-sm min-w-[900px]">
                        <thead className="bg-[var(--surface-muted)] border-b border-[var(--border)]">
                          <tr>
                            <th className="px-3 py-2 text-xs font-semibold text-[var(--fg-muted)] text-left">Name</th>
                            <th className="px-3 py-2 text-xs font-semibold text-[var(--fg-muted)] text-left">Relationship</th>
                            <th className="px-3 py-2 text-xs font-semibold text-[var(--fg-muted)] text-left">Phones</th>
                            <th className="px-3 py-2 text-xs font-semibold text-[var(--fg-muted)] text-left">Addresses</th>
                            <th className="px-3 py-2 text-xs font-semibold text-[var(--fg-muted)] text-left">Emails</th>
                          </tr>
                        </thead>
                        <tbody>
                          {fhRelationsParsed.length === 0 ? (
                            <tr>
                              <td colSpan={5} className="p-3 text-xs text-gray-500">
                                No relations added.
                              </td>
                            </tr>
                          ) : (
                            fhRelationsParsed.map((r, idx) => {
                              const phones = (Array.isArray(r?.phones) ? r.phones : r?.phone ? [r.phone] : [])
                                .map((p) => onlyDigits(p))
                                .filter(Boolean);

                              const emails = (Array.isArray(r?.emails) ? r.emails : r?.email ? [r.email] : [])
                                .map((e) => toStr(e).trim())
                                .filter(Boolean);

                              const addresses = (Array.isArray(r?.addresses) ? r.addresses : r?.address ? [r.address] : [])
                                .map((a) => toStr(a).trim())
                                .filter(Boolean);

                              return (
                                <tr key={idx} className="border-t">
                                  <td className="p-2">{(r?.name ?? "-") || "-"}</td>
                                  <td className="p-2">{(r?.relationship ?? "-") || "-"}</td>
                                  <td className="p-2">{phones.join(", ") || "-"}</td>
                                  <td className="p-2">{addresses.join(", ") || "-"}</td>
                                  <td className="p-2">{emails.join(", ") || "-"}</td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  </>
                );
              })()}
            </div>
          ) : (
            <div className="mt-3 text-sm text-gray-600">
              No family history yet. Click Edit to add members and contacts.
            </div>
          )}
        </div>
      </div>

      <div className="mt-6">
        <div className="surface-card rounded-2xl p-4">
          <div className="flex items-start justify-between mb-3 gap-3">
            <div>
              <h3 className="font-semibold text-lg mb-0">Status & Notes</h3>
              <div className="text-xs text-gray-500">Write notes (HTML supported).</div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowPasteHtml(true)}
                className="px-2 py-1 text-xs border rounded bg-white"
                title="Paste raw HTML"
              >
                Paste HTML
              </button>
            </div>
          </div>

          <div className="flex gap-2 mb-3">
            {["Approved", "Rejected"].map((status) => (
              <button
                key={status}
                onClick={() => {
                  setActiveStatus(status);
                  setFollowUpDate("");
                  setSubStatus("NA");
                  setSubStatusManuallySet(true);
                }}
                className={`px-2 py-1 rounded text-sm font-medium ${
                  activeStatus === status ? "bg-indigo-600 text-white" : "bg-white border"
                }`}
              >
                {status === "Approved" ? "Lead" : "Rejected"}
              </button>
            ))}
          </div>

          {activeStatus === "Approved" && (
            <>
              <div className="flex gap-2 mb-3">
                <div className="flex-1">
                  <label className="text-sm font-medium block mb-1">Sub-Status</label>
                  <select
                    value={subStatus}
                    onChange={(e) => onSubStatusChange(e.target.value)}
                    className="w-full border px-2 py-1 rounded text-sm"
                  >
                    {subStatusOptions.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex-1">
                  <label className="text-sm font-medium block mb-1">Label</label>
                  <select
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    className="w-full border px-2 py-1 rounded text-sm"
                  >
                    <option value="">Select label...</option>
                    <option value="Hot">Hot</option>
                    <option value="Warm">Warm</option>
                    <option value="Cold">Cold</option>
                  </select>
                </div>
              </div>

              {subStatus === "Follow Up" && subStatusManuallySet && showCalendar && (
                <div className="mb-3">
                  <label className="text-sm block mb-1">Select Follow-up Date</label>
                  <DatePicker
                    selected={followUpDate ? new Date(followUpDate) : null}
                    onChange={(date) => handleFollowUpPick(date)}
                    dateFormat="MM/dd/yyyy"
                    className="border px-2 py-1 rounded text-sm w-full"
                    placeholderText="Select a date"
                    inline
                  />
                </div>
              )}
            </>
          )}

          <div className="text-sm text-gray-700 mb-2">
            Current Status: <span className="font-semibold">{activeStatus}</span>
          </div>

          <div className="mb-3 bg-white p-2 rounded">
            {appliedRawHtml ? (
              <div className="w-full">
                <div className="mb-2 flex items-center gap-2">
                  <button
                    onClick={() => {
                      setAppliedRawHtml("");
                      if (editorRef.current) editorRef.current.innerHTML = "";
                    }}
                    className="px-2 py-1 border rounded text-sm"
                  >
                    Clear Raw HTML
                  </button>
                  <button
                    onClick={() => {
                      if (!appliedRawHtml) {
                        toast.error("No raw HTML to open in editor.");
                        return;
                      }
                      if (editorRef.current) editorRef.current.innerHTML = appliedRawHtml;
                      setAppliedRawHtml("");
                      toast.info("Opened raw HTML in editor for editing.");
                    }}
                    className="px-2 py-1 bg-indigo-600 text-white rounded text-sm"
                  >
                    Open in Editor
                  </button>

                  <div className="text-xs text-gray-500 ml-auto">Preview (raw HTML preserved)</div>
                </div>

                <div
                  className="raw-html-preview border rounded p-2"
                  style={{ minHeight: "200px", maxWidth: "100%", overflowX: "auto", overflowY: "auto" }}
                  dangerouslySetInnerHTML={{ __html: appliedRawHtml }}
                />
              </div>
            ) : (
              <div
                ref={editorRef}
                contentEditable
                onPaste={handleEditorPaste}
                suppressContentEditableWarning
                style={{
                  minHeight: 200,
                  border: "1px solid #e5e7eb",
                  padding: 8,
                  borderRadius: 6,
                  background: "#fff",
                  overflow: "auto",
                  maxWidth: "100%",
                }}
                className="text-sm"
                aria-label="Rich editor"
              />
            )}
          </div>

          <div className="mb-3">
            <label className="text-sm font-medium block mb-1">Attach PDF (optional)</label>
            <input type="file" accept="application/pdf" onChange={handlePdfUpload} disabled={uploadingPdf} />
            {uploadedPdfUrl && (
              <div className="mt-1 text-xs text-green-600">
                Attached:{" "}
                <a
                  href={uploadedPdfUrl.startsWith("http") ? uploadedPdfUrl : `${BASE_URL}${uploadedPdfUrl}`}
                  target="_blank"
                  rel="noreferrer"
                  className="underline text-blue-600"
                >
                  View PDF
                </a>
              </div>
            )}
          </div>

          <div className="flex gap-2 mb-3 items-center">
            <button
              onClick={handleSaveStatus}
              disabled={saveLoading || (activeStatus === "Approved" && (!label || label === ""))}
              className={`bg-gradient-to-r from-green-400 to-blue-500 text-white text-sm px-3 py-1 rounded font-medium self-start hover:from-green-500 hover:to-blue-600 ${
                saveLoading ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {saveLoading ? "Saving..." : "Save Status"}
            </button>

            <button
              onClick={() => {
                if (editorRef.current) editorRef.current.innerHTML = "";
                setAppliedRawHtml("");
                setCurrentNoteId(null);
                setUploadedPdfUrl(null);
              }}
              className="text-sm px-3 py-1 border rounded"
            >
              Clear
            </button>

            <div className="text-xs text-gray-500 self-center">
              Notes save raw HTML (if pasted) or editor HTML. You can optionally attach a PDF.
            </div>
          </div>

          <div className="overflow-auto max-h-[40vh]">
            {notes.length === 0 && <div className="text-xs text-gray-500">No notes yet</div>}
            {notes.map((n) => (
              <NoteItem
                key={n.id || n.timestamp}
                note={n}
                onEdit={loadNoteIntoEditorForEdit}
                onDelete={deleteNote}
                onUpdate={(newText) => updateNoteInline(n, newText)}
                renderNoteContent={(text) => {
                  if (!text) return { __html: "-" };
                  const looksLikeHtml = /<\/?[a-z][\s\S]*>/i.test(text);
                  if (looksLikeHtml) return { __html: sanitizeForView(text) };
                  return { __html: toStr(text).replace(/\n/g, "<br/>") };
                }}
              />
            ))}
          </div>
        </div>
      </div>

      {showPasteHtml && (
        <div className="fixed inset-0 z-70 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-3xl rounded p-4">
            <h3 className="font-semibold mb-2">Paste raw HTML here</h3>
            <textarea
              value={pasteHtmlText}
              onChange={(e) => setPasteHtmlText(e.target.value)}
              className="w-full h-64 border p-2 mb-3"
              placeholder="Paste full HTML block (including inline styles/classes)"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowPasteHtml(false);
                  setPasteHtmlText("");
                }}
                className="px-3 py-1 border rounded"
              >
                Cancel
              </button>
              <button onClick={applyPastedHtml} className="px-3 py-1 bg-indigo-600 text-white rounded">
                Apply HTML (preserve styles)
              </button>
            </div>
          </div>
        </div>
      )}

      <DetailModal
        open={detailModalOpen}
        row={detailRow}
        onClose={() => {
          setDetailModalOpen(false);
          setDetailRow(null);
        }}
      />

      <FamilyHistoryModal
        open={fhModalOpen}
        onClose={() => setFhModalOpen(false)}
        onSave={saveFamilyHistoryManual}
        saving={fhSaving}
        summary={fhSummary}
        setSummary={setFhSummary}
        members={fhMembers}
        setMembers={setFhMembers}
      />
    </div>
  );
}
